import React, {useState} from "react";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";

/**
 * CustomDownloadButton
 *
 * options: [{ id: string, label?: ReactNode, disabled?: boolean, ... }]
 *
 * Controlled / Uncontrolled:
 * - value / defaultValue + onChange
 * - open / defaultOpen + onOpenChange
 */
function useControlled({ controlled, defaultValue }) {
    const [state, setState] = React.useState(defaultValue);
    const isControlled = controlled !== undefined;
    const value = isControlled ? controlled : state;

    const setValue = React.useCallback(
        (next) => {
            if (!isControlled) setState(next);
        },
        [isControlled]
    );

    return [value, setValue];
}

export default function CustomSplitButton({
                                                 options = [],

                                                 /* selection by id */
                                                 value,
                                                 defaultValue,
                                                 onChange, // ({ id, option, event })

                                                 /* actions */
                                                 onClickMain, // ({ id, option, event })

                                                 /* label strategy */
                                                 getOptionLabel,

                                                 /* misc */
                                                 disabled = false,
                                                 toggleIcon = <ArrowDropDownIcon />,

                                                 /* pass-through MUI props */
                                                 buttonGroupProps,
                                                 mainButtonProps,
                                                 toggleButtonProps,
                                                 popperProps,
                                                 paperProps,
                                                 menuListProps,
                                                 menuItemProps,
                                             }) {
    const anchorRef = React.useRef(null);

    const computedDefault =
        defaultValue !== undefined
            ? defaultValue
            : options[0]?.id ?? null;

    const [selectedId, setSelectedId] = useControlled({
        controlled: value,
        defaultValue: computedDefault,
    });

    const [isOpen, setIsOpen] = useState(false)

    const setOpen = React.useCallback(
        (next) => {
            setIsOpen(next);
        },
        [setIsOpen]
    );

    const currentOption = React.useMemo(
        () => options.find((o) => o.id === selectedId) ?? null,
        [options, selectedId]
    );

    const resolveLabel = React.useCallback(
        (opt) => {
            if (!opt) return "—";
            if (getOptionLabel) return getOptionLabel(opt);
            if (opt.label !== undefined) return opt.label;
            return opt.id;
        },
        [getOptionLabel]
    );

    const handleMainClick = (event) => {
        if (!currentOption) return;
        if (onClickMain)
            onClickMain({ id: currentOption.id, option: currentOption, event });
    };

    const handleToggle = () => setOpen(!isOpen);

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) return;
        setOpen(false);
    };

    const handleSelect = (event, opt) => {
        if (disabled || opt.disabled) return;

        setSelectedId(opt.id);
        if (onChange) onChange({ id: opt.id, option: opt, event });

        setOpen(false);
    };

    return (
        <>
            <ButtonGroup
                variant="contained"
                ref={anchorRef}
                aria-label="Custom download button"
                disabled={disabled}
                {...buttonGroupProps}
            >
                <Button
                    onClick={handleMainClick}
                    disabled={disabled || !currentOption}
                    {...mainButtonProps}
                    sx={{ justifyContent: 'flex-start', ...mainButtonProps?.sx }}
                >
                    <span style={{ display: 'inline-grid', justifyItems: 'start' }}>
                        {options.map((opt) => (
                            <span
                                key={opt.id}
                                style={{
                                    gridArea: '1 / 1',
                                    visibility: opt.id === selectedId ? 'visible' : 'hidden',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {resolveLabel(opt)}
                            </span>
                        ))}
                    </span>
                </Button>

                <Button
                    size="small"
                    aria-controls={isOpen ? "custom-download-menu" : undefined}
                    aria-expanded={isOpen ? "true" : undefined}
                    aria-haspopup="menu"
                    onClick={handleToggle}
                    disabled={disabled || options.length === 0}
                    {...toggleButtonProps}
                >
                    {toggleIcon}
                </Button>
            </ButtonGroup>

            <Popper
                sx={{ zIndex: 1300 }}
                open={isOpen}
                anchorEl={anchorRef.current}
                transition
                disablePortal
                modifiers={[{
                    name: 'sameWidth',
                    enabled: true,
                    phase: 'beforeWrite',
                    requires: ['computeStyles'],
                    fn: ({ state }) => { state.styles.popper.width = `${state.rects.reference.width}px`; },
                }]}
                {...popperProps}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === "bottom" ? "center top" : "center bottom",
                        }}
                    >
                        <Paper sx={{ bgcolor: 'background.default' }} {...paperProps}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList
                                    id="custom-download-menu"
                                    autoFocusItem={isOpen}
                                    {...menuListProps}
                                >
                                    {options.map((opt) => {
                                        const extra =
                                            typeof menuItemProps === "function"
                                                ? menuItemProps(opt)
                                                : menuItemProps;

                                        return (
                                            <MenuItem
                                                key={opt.id}
                                                selected={opt.id === selectedId}
                                                disabled={disabled || !!opt.disabled}
                                                onClick={(e) => handleSelect(e, opt)}
                                                {...extra}
                                            >
                                                {resolveLabel(opt)}
                                            </MenuItem>
                                        );
                                    })}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
}
